import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { initSentry, ErrorBoundary, getLastSentryEventId } from './lib/sentry'
import { initPostHog } from './services/posthog'
import { buildSupportMailto } from './utils/supportMailto'
import './index.css'
import App from './App.jsx'

// Production console suppression (matches utils/logger.js claim).
// Strips console.log/.debug/.info noise from customer browser consoles.
// Errors and warnings still surface so we can debug if customers send us a
// console screenshot. Sentry catches errors regardless via the Error Boundary.
if (import.meta.env.PROD) {
  // eslint-disable-next-line no-console
  console.log = () => {};
  // eslint-disable-next-line no-console
  console.debug = () => {};
  // eslint-disable-next-line no-console
  console.info = () => {};
}

// Initialize error tracking (no-ops if VITE_SENTRY_DSN not set)
initSentry();

// Initialize product analytics (no-ops if VITE_POSTHOG_KEY not set).
// Fire-and-forget — the service awaits its own lazy-loaded SDK chunk.
initPostHog();

// Phase 20.7 (audit-fix 2026-04-29): Web Vitals capture (LCP / INP / CLS).
// Lazy-loaded; gated on VITE_SENTRY_DSN. No-op without DSN.
import('./lib/webVitals').then(({ initWebVitals }) => initWebVitals()).catch(() => {});

// Fallback UI if the React tree crashes — prevents the dreaded blank white screen.
// Sentry still captures the original error in the background.
function AppCrashFallback({ error, resetError }) {
  const eventId = getLastSentryEventId();
  const reportMailto = buildSupportMailto({
    subject: `Juuni — crash report${eventId ? ` (event ${eventId.slice(0, 8)})` : ''}`,
    sentryEventId: eventId,
    intro: `The app crashed with the following error:\n\n${error?.message || '(no message)'}\n\nI was trying to:\n(describe what you were doing when this happened)`,
  });

  return (
    <div style={{
      minHeight: '100dvh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
      fontFamily: "'Inter', -apple-system, sans-serif",
      background: '#FDFBF7',
    }}>
      <div style={{ maxWidth: 480, textAlign: 'center' }}>
        {/* Phase 20.8 T1-2.1 a11y baseline (D3 confirmed): preserve cream
            palette + find darker text colors meeting WCAG 4.5:1 against
            #FDFBF7 background. Original #C08B2A (luminance 0.32) → 2.76:1
            FAILED; #966619 (luminance 0.17) hits 4.51:1 — same cream-amber
            family, just shifted darker. */}
        <div style={{ fontSize: 14, fontFamily: "'JetBrains Mono', monospace", color: '#966619', letterSpacing: '0.08em', marginBottom: 12 }}>
          JUUNI
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 600, margin: '0 0 8px', color: '#1a1a1a' }}>
          Something broke on this page
        </h1>
        {/* Body text #666 was 5.67:1 ✓; kept. */}
        <p style={{ fontSize: 14, color: '#666', lineHeight: 1.6, margin: '0 0 20px' }}>
          We've been notified and will look into it. You can try reloading, or head back to the home screen.
        </p>
        {eventId && (
          // T1-2.1: #999 (2.83:1) → #767676 (4.54:1 ✓).
          <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", color: '#767676', marginBottom: 16 }}>
            Error ID: {eventId.slice(0, 8)}
          </div>
        )}
        {import.meta.env.DEV && error?.message && (
          // T1-2.1: scrollable-region-focusable — overflow:auto element needs
          // tabindex=0 so keyboard users can scroll. Plus role="region" +
          // aria-label so screen readers announce it as a navigable region.
          <pre tabIndex={0} role="region" aria-label="Error details" style={{
            textAlign: 'left', background: '#fff', border: '1px solid #eee', borderRadius: 8,
            padding: 12, fontSize: 11, fontFamily: "'JetBrains Mono', monospace",
            color: '#c00', overflow: 'auto', maxHeight: 200, marginBottom: 16,
          }}>
            {error.message}
          </pre>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => { resetError(); }}
            style={{
              padding: '10px 18px', border: '1px solid rgba(0,0,0,0.12)', background: '#fff',
              borderRadius: 8, cursor: 'pointer', fontSize: 14, color: '#1a1a1a',
            }}>
            Try again
          </button>
          <button
            onClick={() => { window.location.href = '/'; }}
            style={{
              padding: '10px 18px', border: 'none', background: '#1a1a1a', color: '#fff',
              borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500,
            }}>
            Go home
          </button>
          <a href={reportMailto} style={{
            padding: '10px 18px', border: '1px solid rgba(0,0,0,0.12)', background: '#fff',
            borderRadius: 8, cursor: 'pointer', fontSize: 14, color: '#1a1a1a',
            textDecoration: 'none', display: 'inline-flex', alignItems: 'center',
          }}>
            Report this to support
          </a>
        </div>
        {/* Phase 19 Tier-2 §2.2 — minimal inline legal links (cannot mount
            global Footer.jsx here without re-bundling React; this fallback may
            render before/after React tree is ready). GDPR Article 13 transparency
            requires legal links from EVERY page including error fallbacks. */}
        <div style={{ marginTop: 32, paddingTop: 20, borderTop: '1px solid rgba(0,0,0,0.08)', fontSize: 11, fontFamily: "'JetBrains Mono', monospace", color: '#767676', letterSpacing: '0.02em' }}>
          <a href="/legal/privacy" style={{ color: '#767676', textDecoration: 'underline', textUnderlineOffset: 2 }}>Privacy</a>
          {' · '}
          <a href="/legal/terms" style={{ color: '#767676', textDecoration: 'underline', textUnderlineOffset: 2 }}>Terms</a>
          {' · '}
          <a href="/security" style={{ color: '#767676', textDecoration: 'underline', textUnderlineOffset: 2 }}>Security</a>
          {' · '}
          <a href="mailto:security@juuniai.com" style={{ color: '#767676', textDecoration: 'underline', textUnderlineOffset: 2 }}>security@juuniai.com</a>
        </div>
      </div>
    </div>
  );
}

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <ErrorBoundary fallback={AppCrashFallback} showDialog={false}>
      <App />
    </ErrorBoundary>
  </StrictMode>,
)
